import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/common/DataTable";
import Pagination from "@/components/common/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Building2 } from "lucide-react";

export default function Accounts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", account_type: "Clinic", phone: "", email: "", city: "", address: "" });
  const [busy, setBusy] = useState(false);

  const canCreate = ["super_admin", "manager", "field_executive"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const p = { page, page_size: 15 };
      if (q) p.q = q;
      const { data } = await api.get("/accounts", { params: p });
      setData(data);
    } catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [q, page]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/accounts", form);
      toast.success("Account created");
      setOpen(false);
      setForm({ name: "", account_type: "Clinic", phone: "", email: "", city: "", address: "" });
      navigate(`/accounts/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const columns = [
    { key: "name", label: "Account", render: (r) => <div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.account_type}</div></div> },
    { key: "phone", label: "Phone", mobileHide: true, render: (r) => r.phone || "—" },
    { key: "city", label: "City", render: (r) => r.city || "—" },
    { key: "contacts", label: "Contacts", mobileHide: true, render: (r) => (r.contacts?.length || 0) },
    { key: "owner_name", label: "Owner", mobileHide: true, render: (r) => r.owner_name || "—" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Accounts" subtitle="Clinics, hospitals and diagnostic centers." testid="accounts-header"
        actions={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal hover:bg-teal-700 h-11" data-testid="add-account-btn"><Plus className="h-4 w-4" /> New Account</Button></DialogTrigger>
            <DialogContent data-testid="account-dialog">
              <DialogHeader><DialogTitle>New account</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" /></div>
                <div><Label>Type</Label>
                  <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Clinic", "Hospital", "Diagnostic Center", "Chain"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>City</Label><Input className="mt-1.5" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                </div>
                <div><Label>Email</Label><Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Address</Label><Input className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="save-account-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search accounts…" className="pl-9 h-11" data-testid="accounts-search" />
        </div>
      </div>
      <DataTable columns={columns} rows={data.items} loading={loading} error={error} onRetry={load}
        onRowClick={(r) => navigate(`/accounts/${r.id}`)} testid="accounts-table"
        empty={{ title: "No accounts yet", description: "Create clinics and hospitals to manage contacts and deals.", icon: Building2 }} />
      <Pagination page={page} pageSize={15} total={data.total} onPage={setPage} />
    </div>
  );
}
